import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "./ProfileSetupModal.css";

const QuestionnaireModal = ({ isOpen, onClose, onSubmit, role, userId }) => {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [customInputs, setCustomInputs] = useState({});
  const [fileUploads, setFileUploads] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && role) {
      fetchQuestions();
    }
  }, [isOpen, role]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://api.biznetusa.com/api/get-questions/${role.role_name}`
      );
      const data = await response.json();

      if (data.status === 200 && data.questions) {
        // Take at least 5 questions, or all if less than 5
        const questionsToShow = data.questions.slice(0, Math.max(5, data.questions.length));
        setQuestions(questionsToShow);
      } else {
        toast.error("Failed to fetch questions");
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
      toast.error("Failed to load questions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelection = (questionId, optionId, isCustom = false) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        answer_id: optionId,
        isCustom: isCustom,
      },
    }));
  };

  const handleCustomInput = (questionId, value) => {
    setCustomInputs((prev) => ({
      ...prev,
      [questionId]: value,
    }));

    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        answer_id: value,
        isCustom: true,
      },
    }));
  };

  const handleFileUpload = (questionId, optionId, file) => {
    setFileUploads((prev) => ({
      ...prev,
      [questionId]: file,
    }));

    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        answer_id: optionId,
        isCustom: false,
        file: file,
      },
    }));
  };

  const handleSubmit = async () => {
    // Validate all questions are answered
    const unansweredQuestions = questions.filter((q) => !answers[q.id]);
    if (unansweredQuestions.length > 0) {
      toast.warning(`Please answer all questions (${unansweredQuestions.length} remaining)`);

      // Scroll to first unanswered question
      const firstUnanswered = document.getElementById(`question-${unansweredQuestions[0].id}`);
      if (firstUnanswered) {
        firstUnanswered.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    try {
      setSubmitting(true);

      // Format answers for API - backend expects question_id and answer_id as strings
      const formattedAnswers = Object.keys(answers).map((questionId) => ({
        question_id: String(questionId),
        answer_id: String(answers[questionId].answer_id),
        isCustom: answers[questionId].isCustom || false,
      }));

      // Backend expects user_role as string (role ID)
      const payload = {
        user_id: String(userId),
        user_role: String(role.id),
        answers: formattedAnswers,
      };

      console.log("Submitting payload:", payload); // Debug log

      const token = localStorage.getItem("token");
      const response = await fetch(
        "https://api.biznetusa.com/api/profile-setup",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token || "",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (data.status === 200) {
        toast.success("Profile setup completed successfully!");

        // Update localStorage with role and permissions
        localStorage.setItem("roles", String(role.id));

        // Get permissions from the role object
        if (role.permissions && role.permissions.length > 0) {
          localStorage.setItem("permissions", JSON.stringify(role.permissions));
        } else {
          localStorage.setItem("permissions", JSON.stringify([]));
        }

        // Store user role name
        localStorage.setItem("user_role_name", role.role_name);

        // Trigger storage event for other components
        window.dispatchEvent(new Event('storage'));

        onSubmit(data);
      } else if (data.status === "error") {
        // Validation errors from backend
        const errors = data.errors;
        const errorMessages = Object.values(errors).flat().join(", ");
        toast.error(errorMessages || "Validation failed");
      } else {
        toast.error(data.message || "Failed to complete profile setup");
      }
    } catch (error) {
      console.error("Error submitting profile setup:", error);
      toast.error("Failed to submit profile. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const answeredCount = Object.keys(answers).length;
  const progress = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;

  return (
    <div className="profile-modal-overlay">
      <div className="profile-modal-container questionnaire-modal-multi">
        <div className="profile-modal-content">
          {/* Header */}
          <div className="profile-modal-header">
            <h2 className="profile-modal-title">
              {role?.role_name} Questionnaire
            </h2>
            <p className="profile-modal-subtitle">
              {answeredCount} of {questions.length} questions answered
            </p>

            {/* Progress Bar */}
            <div className="progress-bar-container">
              <div
                className="progress-bar-fill"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          {/* Body */}
          <div className="profile-modal-body questionnaire-body-multi">
            {loading ? (
              <div className="profile-modal-loading">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading questions...</p>
              </div>
            ) : questions.length === 0 ? (
              <div className="no-questions">
                <p>No questions available for this role.</p>
                <button
                  className="btn btn-primary"
                  onClick={() => handleSubmit()}
                >
                  Continue
                </button>
              </div>
            ) : (
              <div className="questions-list">
                {questions.map((question, index) => {
                  const isAnswered = !!answers[question.id];

                  return (
                    <div
                      key={question.id}
                      id={`question-${question.id}`}
                      className={`question-item ${isAnswered ? 'question-answered' : 'question-unanswered'}`}
                    >
                      {/* Question Number & Text */}
                      <div className="question-header">
                        <span className="question-number">Question {index + 1}</span>
                        {isAnswered && (
                          <span className="answered-badge">
                            <i className="fas fa-check-circle"></i> Answered
                          </span>
                        )}
                      </div>
                      <h3 className="question-text">{question.question}</h3>

                      {/* Options */}
                      <div className="options-container">
                        {question.options.map((option) => {
                          const isSelected =
                            answers[question.id]?.answer_id === option.option_value;

                          return (
                            <div
                              key={option.id}
                              className={`option-card ${
                                isSelected ? "option-card-active" : ""
                              }`}
                              onClick={() =>
                                handleAnswerSelection(
                                  question.id,
                                  option.option_value,
                                  false
                                )
                              }
                            >
                              <div className="option-content">
                                <span className="option-text">
                                  {option.option_value}
                                </span>
                              </div>
                              {isSelected && (
                                <div className="option-checkmark">
                                  <i className="fas fa-check-circle"></i>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Custom Input Field - Full Width Below Grid */}
                      {answers[question.id] &&
                        question.options.some(opt =>
                          answers[question.id]?.answer_id === opt.option_value &&
                          (opt.option_value.toLowerCase().includes("other") ||
                           opt.option_value.toLowerCase().includes("specify"))
                        ) && (
                        <div className="custom-input-container-full">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Please specify..."
                            value={customInputs[question.id] || ""}
                            onChange={(e) =>
                              handleCustomInput(question.id, e.target.value)
                            }
                          />
                        </div>
                      )}

                      {/* File Upload - Full Width Below Grid */}
                      {answers[question.id] &&
                        question.options.some(opt =>
                          answers[question.id]?.answer_id === opt.option_value &&
                          (opt.option_value.toLowerCase().includes("upload") ||
                           opt.option_value.toLowerCase().includes("file"))
                        ) && (
                        <div className="custom-input-container-full">
                          <input
                            type="file"
                            className="form-control"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={(e) =>
                              handleFileUpload(
                                question.id,
                                answers[question.id].answer_id,
                                e.target.files[0]
                              )
                            }
                          />
                          {fileUploads[question.id] && (
                            <small className="text-success mt-1 d-block">
                              File selected: {fileUploads[question.id].name}
                            </small>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {!loading && questions.length > 0 && (
            <div className="profile-modal-footer">
              <div className="footer-info">
                <span className="progress-text">
                  {answeredCount === questions.length ? (
                    <span className="text-success">
                      <i className="fas fa-check-circle"></i> All questions answered
                    </span>
                  ) : (
                    <span>
                      {questions.length - answeredCount} question{questions.length - answeredCount !== 1 ? 's' : ''} remaining
                    </span>
                  )}
                </span>
              </div>
              <button
                className="btn btn-primary btn-lg w-100"
                onClick={handleSubmit}
                disabled={submitting || answeredCount !== questions.length}
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane me-2"></i>
                    Complete Profile Setup
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireModal;
